import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
  Label,
} from '@parrot-software/design-system';
import { Loader2 } from '@parrot-software/design-system/icons';

const strongComponents = { strong: <strong className="font-semibold" /> };

interface DeleteSubscriptionBlockDialogProps {
  blockName: string;
  open: boolean;
  isLoading?: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation dialog for deleting (dissolving) a subscription block group.
 *
 * Lists the consequences of dissolving the group and requires the user to type
 * the exact group name before the destructive action becomes enabled.
 *
 * @param {DeleteSubscriptionBlockDialogProps} props - Component properties
 * @returns {React.ReactElement} AlertDialog with a name-confirmation input
 */
export const DeleteSubscriptionBlockDialog = ({
  blockName,
  open,
  isLoading = false,
  onConfirm,
  onOpenChange,
}: DeleteSubscriptionBlockDialogProps): React.ReactElement => {
  const { t } = useTranslation('subscriptions');
  const [confirmValue, setConfirmValue] = useState('');

  useEffect(() => {
    if (!open) setConfirmValue('');
  }, [open]);

  const isConfirmDisabled = confirmValue.trim() !== blockName.trim() || isLoading;

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className="max-w-[480px] gap-4 rounded-lg p-6 font-sans shadow-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-semibold">{t('blockDelete.dialog.title')}</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            {t('blockDelete.dialog.intro')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            <Trans components={strongComponents} i18nKey="subscriptions:blockDelete.dialog.reason1" />
          </li>
          <li>
            <Trans components={strongComponents} i18nKey="subscriptions:blockDelete.dialog.reason2" />
          </li>
          <li>
            <Trans components={strongComponents} i18nKey="subscriptions:blockDelete.dialog.reason3" />
          </li>
        </ol>

        <div className="flex flex-col gap-1">
          <Label className="text-sm font-medium text-foreground" htmlFor="delete-block-confirm">
            {t('blockDelete.dialog.confirmInputLabel')}
          </Label>
          <Input
            autoComplete="off"
            id="delete-block-confirm"
            onChange={(event) => setConfirmValue(event.target.value)}
            placeholder={t('blockDelete.dialog.confirmInputPlaceholder')}
            type="text"
            value={confirmValue}
          />
        </div>

        <AlertDialogFooter className="flex justify-end gap-4">
          <AlertDialogCancel disabled={isLoading}>{t('blockDelete.dialog.cancelButton')}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            disabled={isConfirmDisabled}
            onClick={onConfirm}
          >
            {t('blockDelete.dialog.confirmButton')}
            {isLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
